import { ReactElement, useMemo } from 'react'
import Main from './components/Main'
import './App.css'
import { createTheme, ThemeProvider, useMediaQuery } from '@material-ui/core'

const App = (): ReactElement => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          type: prefersDarkMode ? 'dark' : 'light',
          primary: prefersDarkMode
            ? {
                light: '#5C6BC0',
                main: '#1A237E',
                dark: '#0D1642',
                contrastText: '#FFFFFF'
              }
            : {
                light: '#5C6BC0',
                main: '#1A237E',
                dark: '#0D1642',
                contrastText: '#FFFFFF'
              },
          secondary: prefersDarkMode
            ? {
                light: '#FF8A80',
                main: '#B71C1C',
                dark: '#7F0000',
                contrastText: '#FFFFFF'
              }
            : {
                light: '#EF5350',
                main: '#B71C1C',
                dark: '#7F0000',
                contrastText: '#FFFFFF'
              },
          background: prefersDarkMode
            ? {
                default: '#0F1229',
                paper: '#1A1E3A'
              }
            : {
                default: '#FFFFFF',
                paper: '#FFFFFF'
              },
          text: prefersDarkMode
            ? {
                primary: '#E8EAF6',
                secondary: '#9FA8DA'
              }
            : {
                primary: '#1A237E',
                secondary: '#3949AB'
              }
        },
        typography: {
          fontFamily:
            "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
          h1: {
            fontWeight: 800,
            letterSpacing: '-0.02em'
          },
          h2: {
            fontWeight: 700,
            letterSpacing: '-0.01em'
          },
          h3: {
            fontWeight: 700
          },
          h4: {
            fontWeight: 600
          },
          h5: {
            fontWeight: 600
          },
          h6: {
            fontWeight: 600
          },
          button: {
            fontWeight: 600,
            textTransform: 'none'
          }
        },
        shape: {
          borderRadius: 8
        },
        overrides: {
          MuiButton: {
            root: {
              borderRadius: 8,
              padding: '10px 24px',
              fontSize: '0.95rem'
            },
            containedPrimary: {
              background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
              boxShadow: '0 4px 14px rgba(26, 35, 126, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D1642 0%, #1A237E 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.45)'
              }
            },
            containedSecondary: {
              background: 'linear-gradient(135deg, #B71C1C 0%, #C62828 100%)',
              boxShadow: '0 4px 14px rgba(183, 28, 28, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7F0000 0%, #B71C1C 100%)',
                boxShadow: '0 6px 20px rgba(183, 28, 28, 0.45)'
              }
            }
          },
          MuiCard: {
            root: {
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(26, 35, 126, 0.08)'
            }
          },
          MuiPaper: {
            rounded: {
              borderRadius: 12
            }
          },
          MuiTextField: {
            root: {
              '& .MuiFilledInput-root': {
                borderRadius: 8,
                backgroundColor: prefersDarkMode
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(26, 35, 126, 0.04)',
                '&:hover': {
                  backgroundColor: prefersDarkMode
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(26, 35, 126, 0.06)'
                },
                '&.Mui-focused': {
                  backgroundColor: prefersDarkMode
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(26, 35, 126, 0.06)'
                }
              }
            }
          },
          MuiAppBar: {
            colorPrimary: {
              backgroundColor: prefersDarkMode ? '#0D1642' : '#FFFFFF',
              color: prefersDarkMode ? '#E8EAF6' : '#1A237E'
            }
          },
          MuiDrawer: {
            paper: {
              backgroundColor: prefersDarkMode ? '#0D1642' : '#FFFFFF',
              borderRight: prefersDarkMode
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(26, 35, 126, 0.08)'
            }
          },
          MuiListItem: {
            root: {
              borderRadius: 8,
              margin: '2px 8px',
              width: 'auto',
              '&.Mui-selected': {
                backgroundColor: prefersDarkMode
                  ? 'rgba(92, 107, 192, 0.2)'
                  : 'rgba(26, 35, 126, 0.08)',
                borderLeft: '3px solid #1A237E',
                '&:hover': {
                  backgroundColor: prefersDarkMode
                    ? 'rgba(92, 107, 192, 0.3)'
                    : 'rgba(26, 35, 126, 0.12)'
                }
              }
            }
          },
          MuiChip: {
            root: {
              fontWeight: 600
            }
          },
          MuiStepper: {
            root: {
              backgroundColor: 'transparent',
              padding: '16px 0'
            }
          },
          MuiStepIcon: {
            root: {
              color: prefersDarkMode
                ? 'rgba(255,255,255,0.2)'
                : 'rgba(26, 35, 126, 0.2)',
              '&$active': {
                color: '#1A237E'
              },
              '&$completed': {
                color: '#2E7D32'
              }
            }
          },
          MuiFab: {
            primary: {
              background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
              boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D1642 0%, #1A237E 100%)'
              }
            }
          }
        }
      }),
    [prefersDarkMode]
  )

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <Main />
      </ThemeProvider>
    </div>
  )
}

export default App
